/** @jsx jsx */
import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import {
	fetchQuery,
	graphql,
	useRelayEnvironment,
	useRefetchableFragment,
	useQueryLoader,
} from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { MenuIdNotifications } from '@jira/navigation-apps__constants/src/MenuIdNotifications.tsx';
import {
	EXPERIENCE_NAVIGATION_TOP_MENU,
	NAVIGATION_ITEM_ID,
	PACKAGE_NAME,
} from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { Notification as NotificationsButton } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/notification-button/index.tsx';
// eslint-disable-next-line jira/import/no-direct-popup
import type { ContentProps } from '@atlaskit/popup/types';
import Popup from '@atlaskit/popup';
import { media } from '@atlaskit/primitives';
import { getDocument } from '@atlaskit/browser-apis';
import { isSupportedUserAgent as isMobileUserAgent } from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import { testIdGenerate, testIdConcat } from '@jira/navigation-apps__common/src/utils/test-id.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useTopMenus } from '@jira/navigation-apps__common/src/controllers/top-menus/index.tsx';
import { useExperienceStart } from '@jira/platform__jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { lazy } from '@atlassian/react-loosely-lazy';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import Nav4NotificationsRefetchableQuery, {
	type Nav4NotificationsRefetchableQuery as Nav4NotificationsRefetchableQueryType,
} from './__generated__/Nav4NotificationsRefetchableQuery.graphql';
import type { Nav4Notifications$key } from './__generated__/Nav4Notifications.graphql';
import type { Nav4NotificationsData$data } from './__generated__/Nav4NotificationsData.graphql';
import { usePolling } from './usePolling.tsx';
import { Nav4NotificationsBadge } from './Nav4NotificationsBadge.tsx';
import { MAX_NOTIFICATION_COUNT } from './constants.tsx';
import { useRegisterNotificationsInCommandPalette } from './command-palette/index.tsx';

const tooltip = defineMessage({
	id: 'atlassian-navigation.notifications.tooltip',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Notifications',
	description: 'Tooltip for notification drawer icon in global-navigation',
});

const label = defineMessage({
	id: 'atlassian-navigation.notifications.label',
	defaultMessage:
		// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
		'{count, plural, =0{ Notifications} one{{count, number} Notification} other{{count, number} Notifications}}',
	description: 'Button label for notification drawer icon in global-navigation',
});

const overNineNotifications = defineMessage({
	id: 'atlassian-navigation.notifications.over-nine-notifications',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Over 9 Notifications',
	description: 'Label for the notification count when there are more than 9 notifications',
});

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyMenu = lazy(
	() =>
		import(
			/* webpackChunkName: "atlassian-navigation.async-notification-menu-native" */ './menu/index.tsx'
		).then(({ Menu }) => Menu),
	{
		ssr: false,
	},
);

type Nav4NotificationsProps = {
	fragmentData: Nav4NotificationsData$data;
};

export const Nav4Notifications = (props: Nav4NotificationsProps): React.JSX.Element => {
	const { fragmentData } = props;
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();

	const collabContextRoutingAri = useMemo(
		() =>
			fg('post-office_enable_routing_workspace_id_prop')
				? PlatformSiteAri.create({ siteId: cloudId }).toString()
				: undefined,
		[cloudId],
	);

	const [_queryRef, loadQuery] = useQueryLoader<Nav4NotificationsRefetchableQueryType>(
		Nav4NotificationsRefetchableQuery,
	);

	const [data] = useRefetchableFragment<
		Nav4NotificationsRefetchableQueryType,
		Nav4Notifications$key
	>(
		graphql`
			fragment Nav4Notifications on Query
			@argumentDefinitions(collabContextRoutingAri: { type: "String" })
			@refetchable(queryName: "Nav4NotificationsRefetchableQuery") {
				notifications {
					unseenNotificationCount(collabContextRoutingAri: $collabContextRoutingAri)
				}
			}
		`,
		fragmentData,
	);

	const { formatMessage } = useIntl();

	// Check for new notifications every 5 minutes. This is currently done through polling because
	// Relay subscriptions are not yet supported server side. This polling will need to be replaced with
	// a subscription in the future (when notifications are migrated to Post Office).
	usePolling(
		useCallback(() => {
			if (getDocument()?.hidden) {
				return;
			}
			fetchQuery(environment, Nav4NotificationsRefetchableQuery, {
				collabContextRoutingAri,
			}).subscribe({
				complete: () => {
					loadQuery({ collabContextRoutingAri }, { fetchPolicy: 'store-and-network' });
				},
				error: (error: Error) => {
					log.safeErrorWithoutCustomerData(
						`${PACKAGE_NAME}.Nav4Notifications`,
						'Error fetching notification',
						error,
					);
				},
			});
		}, [environment, loadQuery, collabContextRoutingAri]),
		300000,
	);

	const fetchedNotificationsCount = data.notifications?.unseenNotificationCount ?? 0;

	// Initialise the state to false on page load, but use an effect to update it if the unseen notification
	// count changes (i.e. as a result of polling) to ensure the badge is shown when there are new notifications.
	const [isNotificationCleared, setIsNotificationCleared] = useState(false);
	useEffect(() => {
		if (fetchedNotificationsCount > 0) {
			setIsNotificationCleared(false);
		}
	}, [fetchedNotificationsCount]);

	// If notifications have been cleared, force the badge to be 0. This crudely addresses a race condition where
	// opening the menu (which resets the unseen notification count) does not complete before a refetch completes.
	const notificationsCount = isNotificationCleared ? 0 : fetchedNotificationsCount;

	const testIdPrefix = testIdGenerate('secondary-actions', 'notifications');

	const [isMenuOpen, { toggle: toggleMenu, off: closeMenu, on: openMenu }] =
		useTopMenus(MenuIdNotifications);

	const content = useCallback(
		({ update }: ContentProps) => (
			<NotificationContentFrame>
				<JSErrorBoundary
					id="notifications.list.async"
					packageName="AtlassianNavigation"
					teamName="navigation"
				>
					<Placeholder name="notification-menu-native" fallback={null}>
						<LazyMenu scheduleUpdate={update} testIdPrefix={testIdPrefix} />
					</Placeholder>
				</JSErrorBoundary>
			</NotificationContentFrame>
		),

		[testIdPrefix],
	);

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.NOTIFICATIONS,
	});

	const onStart = useExperienceStart({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
		experienceId: NAVIGATION_ITEM_ID.NOTIFICATIONS,
		analyticsSource: 'atlassian-navigation',
	});

	const onClick = useCallback(() => {
		if (isMenuOpen) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			performance.clearMarks('notification.list.render.start');
		} else {
			performance.mark('notification.list.render.start');
		}

		onStart();
		toggleMenu();
		triggerAnalytics();

		// Always set isNotificationCleared to true when the menu trigger is clicked
		// that there are no unseen notifications).
		setIsNotificationCleared(true);

		// PLEASE NOTE: Refetching the notifications does not occur here because for some reason
		// it blocks the popup from being closed.
	}, [isMenuOpen, onStart, toggleMenu, triggerAnalytics]);

	useRegisterNotificationsInCommandPalette(onClick);

	const badge = useCallback(
		() => <Nav4NotificationsBadge count={notificationsCount} />,
		[notificationsCount],
	);

	const notificationsLabel =
		notificationsCount < MAX_NOTIFICATION_COUNT
			? formatMessage(label, { count: notificationsCount })
			: formatMessage(overNineNotifications);

	const trigger = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'triggerProps' implicitly has an 'any' type.
		(triggerProps) => (
			<div
				data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
				data-vc="atlassian-navigation-notifications"
			>
				<NotificationsButton
					{...triggerProps}
					badge={badge}
					isSelected={isMenuOpen}
					onClick={onClick}
					tooltip={formatMessage(tooltip)}
					label={notificationsLabel}
					onViewRequests={openMenu}
				/>
			</div>
		),
		[badge, formatMessage, isMenuOpen, notificationsLabel, onClick, openMenu, testIdPrefix],
	);

	// ENGHEALTH-39740: we need to wrap both the popup and trigger as 'listitem' since popup has role 'dialog' which cannot be a direct child of a 'list' parent
	// Any questions on this, reach out to dseong
	return (
		<div role="listitem">
			<Popup
				content={content}
				isOpen={isMenuOpen}
				onClose={closeMenu}
				placement="bottom-end"
				trigger={trigger}
				shouldRenderToParent
				shouldFitViewport={isMobileUserAgent() ? true : undefined}
				autoFocus={false}
			/>
		</div>
	);
};

const notificationContentFrameStyles = css({
	width: 'calc(100vw - 10px)',
	height: 'calc(100vh - 128px)',
	display: 'flex',
	[media.above.xs]: {
		width: '540px',
	},
});

const NotificationContentFrame = ({ children }: { children: ReactNode }) => {
	return <div css={notificationContentFrameStyles}>{children}</div>;
};
